import React from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { keyframes } from "@mui/material/styles";

const planetRotate = keyframes`
  to { transform: rotate(1turn); }
`;

// Two starfields, drawn as box-shadow point lists on the ::before and ::after
// pseudo-elements of a single 0.5vmin dot. Vendor-prefixed copies of these
// lists were dropped when this moved off Sass — emotion prefixes for us.
const WHITE_STARS = [
  "93vw 63vh", "3vw 17vh", "28vw 85vh", "97vw 82vh", "55vw 83vh",
  "74vw 72vh", "84vw 11vh", "32vw 6vh", "45vw 92vh", "98vw 57vh",
  "63vw 98vh", "90vw 46vh", "50vw 86vh", "38vw 21vh", "74vw 2vh",
  "89vw 22vh", "39vw 0vh", "25vw 89vh", "54vw 58vh", "81vw 39vh",
  "51vw 8vh", "24vw 56vh", "50vw 23vh", "62vw 34vh", "10vw 77vh",
  "92vw 45vh", "70vw 40vh", "2vw 53vh", "3vw 54vh", "18vw 21vh",
  "48vw 47vh", "83vw 96vh", "26vw 32vh", "46vw 9vh", "2vw 13vh",
  "29vw 63vh", "17vw 90vh", "78vw 9vh", "15vw 39vh", "90vw 5vh",
]
  .map((pos) => `${pos} .3vmin rgba(255, 255, 255, .8)`)
  .concat("30vw 90vh .2vmin rgba(190, 200, 255, .9)")
  .join(", ");

const RED_STARS = [
  "67vw 35vh", "89vw 13vh", "77vw 62vh", "1vw 74vh", "38vw 15vh",
  "79vw 10vh", "16vw 47vh", "74vw 71vh", "30vw 96vh", "13vw 98vh",
  "9vw 40vh", "9vw 71vh", "12vw 93vh", "91vw 26vh", "73vw 48vh",
  "61vw 46vh", "20vw 3vh", "90vw 59vh", "48vw 72vh", "91vw 68vh",
]
  .map((pos) => `${pos} .2vmin .1vmin rgba(255, 200, 200, .8)`)
  .join(", ");

const star = {
  content: '""',
  position: "absolute",
  top: 0,
  left: 0,
  display: "block",
  width: ".5vmin",
  height: ".5vmin",
  borderRadius: "50%",
  color: "transparent",
} as const;

const LoadingScreen = () => (
  <Box
    sx={{
      position: "fixed",
      zIndex: 999,
      opacity: 0.99,
      top: 0,
      left: 0,
      width: "100vw",
      height: "100%",
    }}
  >
    <Box
      sx={{
        m: 0,
        height: "100%",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: "50vh",
        bgcolor: "secondary.main",
        color: "grey.400",
        "&::before": { ...star, boxShadow: WHITE_STARS },
        "&::after": { ...star, boxShadow: RED_STARS },
      }}
    >
      <Box
        component="h2"
        sx={{
          m: 0,
          height: "12vmin",
          width: "120vmin",
          display: "flex",
          justifyContent: "center",
          position: "relative",
          color: "grey.400",
          "& em": {
            fontFamily: (theme) => theme.typography.h2.fontFamily,
            color: "currentColor",
            position: "relative",
            fontStyle: "normal",
            textAlign: "center",
          },
        }}
      >
        <em>L</em>
        <Box
          component="em"
          sx={{
            animation: `${planetRotate} 4s linear infinite`,
            position: "relative",
            transformOrigin: "50vmin 5vmin",
          }}
        >
          O
        </Box>
        <em>A</em>
        <em>D</em>
        <em>I</em>
        <em>N</em>
        <em>G</em>
      </Box>
    </Box>
  </Box>
);

/**
 * Full-screen splash shown until the app's bootstrap data has landed.
 * Renders nothing once loaded, or if the token was rejected — in that case
 * the router sends the user to /auth/login and the splash would cover it.
 */
const Loading = () => {
  // Selected as two booleans rather than one object: useSelector compares by
  // reference, so returning a fresh object here would re-render on every
  // dispatched action. connect() shallow-compared, so this keeps the old
  // render behaviour.
  const isTokenError = useSelector((state: any) =>
    Boolean(state.metadata.loading.get("tokenError"))
  );
  const isLoaded = useSelector((state: any) => {
    if (!state.user.get("jwt")) return true;
    const loading = state.metadata.loading;
    return Boolean(
      loading.get("appDataLoaded") &&
        loading.get("profileDataLoaded") &&
        loading.get("challengeDataLoaded") &&
        loading.get("projectsLoaded") &&
        loading.get("projectDataLoaded") &&
        loading.get("hubsLoaded") &&
        loading.get("userLoaded")
    );
  });

  if (isLoaded || isTokenError) return null;
  return <LoadingScreen />;
};

export default Loading;
